import React, { useState } from "react";
import { Paper } from "@mui/material";
import { format, startOfWeek, endOfWeek, subWeeks } from "date-fns";

import { ANLoadingScreen } from "@answersnow/pkg-theme";
import useClinicianGetCaseloadSummary from "hooks/api/clinician/useClinicianGetCaseloadSummary";
import { ModuleContainer } from "../styles";
import CaseloadSummaryModule, {
  TIME_RANGES,
  STATUS_OPTIONS,
  DISPLAY_UNITS,
} from "./CaseloadSummaryModule";

type TimeRange = (typeof TIME_RANGES)[keyof typeof TIME_RANGES];
type StatusOption = (typeof STATUS_OPTIONS)[keyof typeof STATUS_OPTIONS];
type DisplayUnit = (typeof DISPLAY_UNITS)[keyof typeof DISPLAY_UNITS];

interface CaseloadSummaryProps {
  clinicianId: string | number | undefined;
  isSmallScreen: boolean;
}

const getDateRange = (
  timeRange: TimeRange,
  customDateRange: [Date | null, Date | null]
): { startDate: Date | null; endDate: Date | null } => {
  const today = new Date();
  if (timeRange === TIME_RANGES.CUSTOM) {
    return {
      startDate: customDateRange[0],
      endDate: customDateRange[1],
    };
  }
  if (timeRange === TIME_RANGES.LAST_WEEK) {
    const lastWeek = subWeeks(today, 1);
    return {
      startDate: startOfWeek(lastWeek, { weekStartsOn: 1 }),
      endDate: endOfWeek(lastWeek, { weekStartsOn: 1 }),
    };
  }
  return {
    startDate: startOfWeek(today, { weekStartsOn: 1 }),
    endDate: endOfWeek(today, { weekStartsOn: 1 }),
  };
};

const CaseloadSummary: React.FC<CaseloadSummaryProps> = ({ clinicianId, isSmallScreen }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGES.THIS_WEEK);
  const [statusFilter, setStatusFilter] = useState<StatusOption>(STATUS_OPTIONS.ALL);
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>(DISPLAY_UNITS.HOURS);
  const [customDateRange, setCustomDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const { startDate, endDate } = getDateRange(timeRange, customDateRange);

  const hasValidDates = startDate && endDate;

  const { caseloadSummary, isCaseloadSummaryLoading } = useClinicianGetCaseloadSummary(
    clinicianId,
    hasValidDates
      ? {
          startDate: format(startDate, "MM/dd/yyyy"),
          endDate: format(endDate, "MM/dd/yyyy"),
          status: statusFilter,
        }
      : {}
  );

  if (isCaseloadSummaryLoading) {
    return (
      <Paper
        component="section"
        elevation={2}
        sx={ModuleContainer}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
        }}
      >
        <ANLoadingScreen height="100%" />
      </Paper>
    );
  }

  return (
    <CaseloadSummaryModule
      data={caseloadSummary}
      timeRange={timeRange}
      onTimeRangeChange={setTimeRange}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      displayUnit={displayUnit}
      onDisplayUnitChange={setDisplayUnit}
      isSmallScreen={isSmallScreen}
      customDateRange={customDateRange}
      onCustomDateRangeChange={setCustomDateRange}
    />
  );
};

export default CaseloadSummary;
